import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  TOUR_SEEN_KEY,
  SECTION_TOURS,
  SECTION_TOURS_DONE_KEY,
  useSectionTours,
  getTourSectionOrder,
} from "../components/GuidedTour";
import {
  PlayCircle, RotateCcw, LayoutDashboard, GraduationCap, Users, Bot, FileCheck,
  Database, Sparkles, Home, ClipboardCheck, Trophy, Calculator, CalendarDays,
  FolderOpen, UserCircle2, ChevronRight, CheckCircle2, ShieldCheck, Lock,
} from "lucide-react";

// Icon per section id (keys match SECTION_TOURS)
const SECTION_ICONS = {
  dashboard: LayoutDashboard,
  welcome: Home,
  course: GraduationCap,
  progress: Trophy,
  exam: ClipboardCheck,
  blueprint: FileCheck,
  assistant: Bot,
  members: Users,
  databases: Database,
  tools: Calculator,
  planner: CalendarDays,
  downloads: FolderOpen,
  account: UserCircle2,
  admin: ShieldCheck,
};

// Preferred display order for hardcoded default sections — funnels a founder
// from "get grounded" to "go deeper". Admin-created customs are appended
// after these, and the admin's drag-drop `order` (returned by /walkthroughs)
// overrides both when set. Hidden/deleted sections never appear because
// useSectionTours already drops them from `tours`.
const SECTION_ORDER = [
  "dashboard", "welcome", "course", "progress", "exam", "blueprint",
  "assistant", "members", "databases", "tools", "planner", "downloads", "account", "admin",
];

function readDone() {
  try {
    const raw = localStorage.getItem(SECTION_TOURS_DONE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

export default function Walkthrough() {
  const [done, setDone] = useState(() => readDone());
  const tours = useSectionTours();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  // Final Exam page shows a "Final exam complete" takeover for users who've
  // already passed — the actual Certified CPG Founder™ exam content isn't
  // rendered on that view, so a walkthrough that lands there can't target
  // anything useful. We detect `completed=true` from `/api/exam/final` and
  // use it to disable the exam section tile's Start Tour button so the
  // founder never hits an unwalkable page.
  const [examCompleted, setExamCompleted] = useState(false);
  const [examProbed, setExamProbed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/exam/final")
      .then((r) => { if (!cancelled) setExamCompleted(!!r?.data?.completed); })
      .catch(() => { /* non-blocking — leave enabled on fetch error */ })
      .finally(() => { if (!cancelled) setExamProbed(true); });
    return () => { cancelled = true; };
  }, []);

  // Refresh done state on window focus + on a custom refresh event so the
  // list stays in sync after a section tour completes.
  useEffect(() => {
    const refresh = () => setDone(readDone());
    window.addEventListener("focus", refresh);
    window.addEventListener("cpg:tour:section-done", refresh);
    // Poll briefly after mount to catch the completion write from the tour
    // callback that fires ~250ms before nav("/walkthrough") lands.
    const iv = setInterval(refresh, 800);
    const stop = setTimeout(() => clearInterval(iv), 4000);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("cpg:tour:section-done", refresh);
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, []);

  const startPlatform = () => window.dispatchEvent(new Event("cpg:tour:start"));
  const resetPlatform = () => window.dispatchEvent(new Event("cpg:tour:reset"));
  const startSection = (id) =>
    window.dispatchEvent(new CustomEvent("cpg:tour:section", { detail: { section: id } }));

  // Compose the visible section list from the tours map (which already
  // reflects the admin's Show/Hide/Delete + any Custom sections created
  // via the walkthroughs console). Priority:
  //   1) admin's saved drag-drop order (any ids not in `tours` are dropped)
  //   2) hardcoded SECTION_ORDER preference for defaults, then
  //      alphabetical for custom ids that still remain.
  // Admin roles always see the admin section (if it survived Hide/Delete).
  const visibleOrder = React.useMemo(() => {
    const availableIds = new Set(
      Object.keys(tours).filter((id) => id !== "admin" || isAdmin),
    );
    const adminOrder = getTourSectionOrder().filter((id) => availableIds.has(id));
    const seen = new Set(adminOrder);
    const preferred = SECTION_ORDER.filter((id) => availableIds.has(id) && !seen.has(id));
    preferred.forEach((id) => seen.add(id));
    const trailing = [...availableIds].filter((id) => !seen.has(id)).sort();
    return [...adminOrder, ...preferred, ...trailing];
  }, [tours, isAdmin]);

  const resetAll = () => {
    try {
      localStorage.removeItem(SECTION_TOURS_DONE_KEY);
    } catch { /* private mode */ }
    setDone(new Set());
  };

  const seenPlatform = (() => {
    try { return !!localStorage.getItem(TOUR_SEEN_KEY); } catch { return false; }
  })();
  const totalSections = visibleOrder.length;
  const completedSections = visibleOrder.filter((id) => done.has(id)).length;
  const pct = totalSections
    ? Math.round((completedSections / totalSections) * 100)
    : 0;

  return (
    <AppShell title="Platform Walkthrough">
      <div className="w-full">
        <PageHero
          eyebrow="Onboarding"
          icon={Sparkles}
          title={<>A guided <span className="text-grad">platform tour</span></>}
          subtitle="Start with the 60-second menu overview, then pick any section below for a deeper walk-through of that page. You can rerun any tour anytime."
        />

        {/* Primary: menu overview */}
        <div className="card-elevated p-6 flex items-center gap-5 flex-wrap" data-testid="walkthrough-cta">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-sky-400 text-white grid place-items-center shadow-lg shrink-0">
            <PlayCircle className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-display text-2xl">Step 1 — Menu overview</p>
            <p className="text-sm text-muted-foreground mt-1">
              We'll highlight each menu item in the sidebar so you know exactly where everything lives.
              {seenPlatform && " You've already completed it once, but feel free to run it again."}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={startPlatform} data-testid="walkthrough-start" className="btn-primary text-sm">
              <PlayCircle className="w-4 h-4" /> Start menu tour
            </button>
            {seenPlatform && (
              <button onClick={resetPlatform} data-testid="walkthrough-reset"
                className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2 inline-flex items-center gap-1">
                <RotateCcw className="w-3.5 h-3.5" /> Replay
              </button>
            )}
          </div>
        </div>

        {/* Progress rail */}
        <div className="mt-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">Step 2 — Deep dives</p>
            <h2 className="font-display text-2xl md:text-3xl mt-2">Pick a section to explore</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Each deep dive takes you to that page and walks you through what everything does — 2 to 5 quick tooltips per section.
            </p>
          </div>
          <div className="min-w-[220px] w-full sm:w-auto" data-testid="walkthrough-progress">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {completedSections} / {totalSections} explored
              </span>
              <span className="font-display text-2xl">{pct}%</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-shell-border/60 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)",
                }}
              />
            </div>
            {completedSections > 0 && (
              <button
                onClick={resetAll}
                data-testid="walkthrough-reset-sections"
                className="mt-2 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Reset progress
              </button>
            )}
          </div>
        </div>

        {/* Section deep-dive grid */}
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleOrder.map((id) => {
            const t = tours[id];
            if (!t) return null;
            const Icon = SECTION_ICONS[id] || Sparkles;
            const isDone = done.has(id);
            const stepCount = t.steps?.length || 0;
            // Lock the exam section tile when the current user has already
            // passed the exam — the destination page renders the
            // completed-state takeover (no exam cards to walk through).
            const isExamLocked = id === "exam" && examCompleted;
            return (
              <button
                key={id}
                onClick={() => { if (isExamLocked) return; startSection(id); }}
                disabled={isExamLocked}
                className={
                  "card-elevated p-5 text-left group relative transition " +
                  (isExamLocked
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:-translate-y-0.5 hover:shadow-lg") +
                  (isDone ? " ring-1 ring-primary/40" : "")
                }
                data-testid={`walkthrough-section-${id}`}
                aria-label={
                  isExamLocked
                    ? `${t.label} tour unavailable — you've already completed the exam`
                    : `Start deep-dive tour for ${t.label}`
                }
                title={
                  isExamLocked
                    ? "You've already passed the final exam. The exam page now shows the completion certificate instead of the exam cards, so this tour has nothing to walk through."
                    : undefined
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  {isExamLocked ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                      data-testid={`walkthrough-section-${id}-locked`}
                    >
                      <Lock className="w-3 h-3" /> Exam completed
                    </span>
                  ) : isDone ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                      data-testid={`walkthrough-section-${id}-done`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> Explored
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      {stepCount} {stepCount === 1 ? "step" : "steps"}
                    </span>
                  )}
                </div>
                <p className="mt-4 font-display text-lg leading-tight">{t.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t.blurb}</p>
                {isExamLocked && (
                  <p className="mt-2 text-[11px] text-amber-800">
                    This tour unlocks on the live exam page — not the completion screen.
                  </p>
                )}
                <span
                  className={
                    "mt-4 inline-flex items-center gap-1 text-xs font-semibold " +
                    (isExamLocked ? "text-muted-foreground" : "text-primary")
                  }
                >
                  {isExamLocked
                    ? "Start tour unavailable"
                    : (isDone ? "Replay tour" : "Start tour")}
                  {!isExamLocked && (
                    <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
